import { useState, useEffect } from 'react';
import _get from 'lodash/get';
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from '@/shared/ui/Modal';
import Button from '@/shared/ui/Button';
import Input from '@/shared/ui/Input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/global/store';

import { 
  useCreateSubaccountMutation, 
  extendedApi,
  useLazyCheckAccountAvailabilityQuery, 
} from '@/shared/api/accounts';
import { isErrorResponse } from '@/global/utils/apiHelpers';
import { setConfirmationCallbackState } from '@/features/Confirmation';
import { sendGtmCustomEvent } from '@/processes/CookieBanner';
import { 
  InvalidFormatMessage, 
  extractError,
  DESCRIPTION_MAX_LENGTH,
} from '@/shared/constants/validation';
// import { writePendingAccountToStorage } from '@/shared/utils/pendingAccounts';
import { CreateAccountValidationSchema, createAccountSchema } from '@/shared/api/accounts/schema';
// import { selectUsername } from '@/processes/Auth';
import { useToggleModalState } from '@/shared/hooks/useToggleModalState';
import { MODALS } from '~/shared/Accounts/constants/modals';
import { useNavigation } from '@/processes/Router';

type CreateSubAccountModalProps = {
}

export const CreateSubAccountModal = ({
}: CreateSubAccountModalProps) => {
  const intl = useIntl();
  const { isShow, closeModal } = useToggleModalState({
    name: MODALS.CREATE_SUBACCOUNT,
  });
  const dispatch = useDispatch<AppDispatch>();
  const { updateURLQueryParams } = useNavigation();
  // const username = useSelector(selectUsername);
  const [createSubaccountMutation, {
    isLoading,
    isError,
    error,
  }] = useCreateSubaccountMutation();
  const [checkAccountAvailability, { 
    isLoading: isAccountAvailabilityLoading 
  }] = useLazyCheckAccountAvailabilityQuery();
  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors },
  } = useForm<CreateAccountValidationSchema>({
    resolver: zodResolver(createAccountSchema),
  });

  const handleSubmitCallback = async (data: CreateAccountValidationSchema) => {
    const { name } = data;
    const response = await checkAccountAvailability(name);
    const isAvailable = _get(response, 'data.available', false) === true;
    if (isAvailable) {
      setError('name', { message: intl.formatMessage({ id: 'error.not.available' }) })
      return;
    }
    dispatch(setConfirmationCallbackState({
      successMessage: intl.formatMessage({ id: 'notify.acc.added' }, {
        name,
      }),
      successCallback: (response: any) => {
        // reset form fields
        reset();
        dispatch(
          extendedApi.util.updateQueryData('getAccounts', null, (draft) => {
            const { about, name } = response?.account || {};
            const existingIndex = draft.findIndex((item: any) => item.name === name);
            // write pending account to storage
            // writePendingAccountToStorage(username, { about, name })
            // update if exits
            if (draft[existingIndex]) {
              Object.assign(draft[existingIndex], { about, name });
            // or, add new
            } else {
              draft.push({ about, name });
            }
          }) as any
        )
        closeModal();
        // choose created account
        updateURLQueryParams({ account: name });
      }
    }))
    const payload = {
      name,
      about: data.about || '',
    }
    createSubaccountMutation(payload);
  }

  const handleOnClose = () => {
    closeModal();
  }


  // TODO: move to hook or util
  useEffect(() => {
    if (isError && isErrorResponse(error)) {
      const errorMsg = error?.data?.message;
      Object.keys(createAccountSchema._def.shape()).forEach((key: any) => {
        if (errorMsg.includes(key)) {
          setError(key, { message: errorMsg });
        }
      })
    }
  }, [isError, error]); // eslint-disable-line

  return (
    <Modal show={isShow} onDismiss={handleOnClose} size="sm">
      <h3 className='text-2xl text-white mb-4'>
        <FormattedMessage id="subacc.create.title" />
      </h3>
      <form onSubmit={handleSubmit(handleSubmitCallback)}>
        <Input
          className='mb-4'
          type="text"
          full={true}
          label={intl.formatMessage({ id: 'subacc.name' })}
          hint={intl.formatMessage({ id: 'subacc.name.tip' })}
          onClick={() => {
            sendGtmCustomEvent({ action: `add_subaccount_popup_name_click` });
          }}
          error={errors.name?.message ? intl.formatMessage({ id: errors.name.message }) : extractError(
            errors.name, 
            { too_small: intl.formatMessage({ id: 'error.min' }, { field: 'Name', count: 3 }) },
          )}
          {...register('name')}
        />
        <Input
          type="text"
          full={true}
          label={intl.formatMessage({ id: 'Description' })}
          labelPostfix={intl.formatMessage({ id: 'optional' })}
          error={extractError(errors.about)}
          maxLength={DESCRIPTION_MAX_LENGTH}
          {...register('about')}
        />
        <div className="flex justify-end mt-6">
          <Button onClick={handleOnClose} type="button" className='min-w-[88px]'><FormattedMessage id="Cancel" /></Button>
          <Button
            isLoading={isLoading || isAccountAvailabilityLoading}
            type="submit" 
            className='ml-5 min-w-[88px]' 
            colorSchema='primary'
          >
            <FormattedMessage id="Save" />
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateSubAccountModal;
